import { DepartmentZone } from 'types/interfaces';
import { RefinementCtx, z, ZodIssueCode } from 'zod';

type ValidationContext = z.RefinementCtx;
type ValidationData = {
  isFias?: boolean;
  cityFias?: any;
  streetFias?: any;
  houseFias?: any;
  customCity?: string;
  customStreet?: string;
  customHouse?: string;
  latitude?: number | null;
  longitude?: number | null;
};

export function validateFias(data: ValidationData, ctx: ValidationContext) {
  if (data.isFias) {
    if (!data.cityFias) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите населенный пункт',
        path: ['cityFias']
      });
    }
    if (!data.streetFias) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите улицу',
        path: ['streetFias']
      });
    }
    if (!data.houseFias) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите номер дома',
        path: ['houseFias']
      });
    }
  }
}

export function validateCustomAddress(
  data: ValidationData,
  ctx: ValidationContext
) {
  if (!data.isFias) {
    const city = data.customCity?.trim();
    const street = data.customStreet?.trim();
    const house = data.customHouse?.trim();

    if (house && !street) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите улицу для дома',
        path: ['customStreet']
      });
    }

    if ((street || house) && !city) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Населенный пункт обязателен при наличии дома или улицы',
        path: ['customCity']
      });
    }
  }
}

type ValidationContextt = z.RefinementCtx & { path?: (string | number)[] };

export function validateCoordinates(
  data: ValidationData,
  ctx: ValidationContext,
  index?: number
) {
  const basePath = index !== undefined ? [...ctx.path, index] : ctx.path;

  // Проверяем, что координаты либо обе заданы, либо обе null/undefined
  const hasLongitude = data.longitude !== null && data.longitude !== undefined;
  const hasLatitude = data.latitude !== null && data.latitude !== undefined;

  if (hasLongitude && !hasLatitude) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Широта обязательна при наличии долготы',
      path: [...basePath, 'latitude']
    });
  }

  if (!hasLongitude && hasLatitude) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Долгота обязательна при наличии широты',
      path: [...basePath, 'longitude']
    });
  }
}

export function validateDestinationArea(
  data: DepartmentZone,
  ctx: ValidationContextt,
  index?: number
) {
  const basePath = ctx.path || [];

  const getErrorPath = (field: string) =>
    index !== undefined ? [...basePath, index, field] : [...basePath, field];

  // Проверка на заполнение хотя бы одного поля
  const hasAnyAddressField =
    data.isFias || data.cityFias
      ? data.cityFias || data.streetFias || data.houseFias
      : data.customCity || data.customStreet || data.customHouse || data.region;

  if (!hasAnyAddressField) {
    const errorField = data.isFias || data.cityFias ? 'cityFias' : 'customCity';
    const errorMessage =
      data.isFias || data.cityFias
        ? 'Необходимо заполнить хотя бы одно поле'
        : 'Необходимо заполнить населенный пункт либо регион';

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: errorMessage,
      path: getErrorPath(errorField)
    });
    return;
  }

  // Валидация в зависимости от режима
  if (data.isFias || data.cityFias) {
    if (!data.cityFias) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите населенный пункт',
        path: getErrorPath('cityFias')
      });
    }
    // Проверка: если есть дом, должна быть улица
    if (data.houseFias && !data.streetFias) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите улицу для дома',
        path: getErrorPath('streetFias')
      });
    }
  } else {
    const city = data.customCity?.trim();
    const street = data.customStreet?.trim();
    const house = data.customHouse?.trim();
    const region = data.region?.trim();
    // Если регион не указан, проверяем населенный пункт
    if (!region && !city) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите регион или населенный пункт',
        path: getErrorPath('customCity')
      });
    }
    // Если указана улица или дом - должен быть населенный пункт (даже если есть регион)
    if ((street || house) && !city) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Населенный пункт обязателен при наличии улицы или дома',
        path: getErrorPath('customCity')
      });
    }
    // Проверка: если есть дом, должна быть улица
    if (house && !street) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите улицу для дома',
        path: getErrorPath('customStreet')
      });
    }
  }
}

export const validateDateRangesOverlap = (
  entries: { startDate: string; endDate: string }[],
  ctx: RefinementCtx,
  pathPrefix: string,
  message = 'Периоды пересекаются',
  field: string = 'overlappingError'
) => {
  const conflictIndexes = new Set<number>();
  const withIndex = entries.map((entry, index) => ({ ...entry, index }));

  for (let i = 0; i < withIndex.length; i++) {
    const a = withIndex[i];
    const aStart = new Date(a.startDate);
    const aEnd = new Date(a.endDate);

    for (let j = i + 1; j < withIndex.length; j++) {
      const b = withIndex[j];
      const bStart = new Date(b.startDate);
      const bEnd = new Date(b.endDate);

      const overlap = aStart <= bEnd && bStart <= aEnd;
      if (overlap) {
        conflictIndexes.add(a.index);
        conflictIndexes.add(b.index);
      }
    }
  }

  conflictIndexes.forEach((index) => {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message,
      path: [pathPrefix, index, field]
    });
  });
};

export const validateDateRangesOverlapWithDepartments = (
  entries: {
    startDate: string;
    endDate: string;
    departmentId?: number | string | null;
  }[],
  ctx: RefinementCtx,
  pathPrefix: string,
  message = 'Периоды пересекаются в одном участке',
  field: string = 'overlappingError'
) => {
  const conflictIndexes = new Set<number>();
  const entriesByDepartment: Record<
    string,
    Array<{
      entry: any;
      index: number;
    }>
  > = {};

  // Группируем записи по участкам
  entries.forEach((entry, index) => {
    const departmentId = entry.departmentId?.toString() || 'default';

    if (!entriesByDepartment[departmentId]) {
      entriesByDepartment[departmentId] = [];
    }

    entriesByDepartment[departmentId].push({
      entry,
      index
    });
  });

  // Проверяем пересечения внутри каждой группы участков
  Object.values(entriesByDepartment).forEach((group) => {
    for (let i = 0; i < group.length; i++) {
      const a = group[i];
      const aStart = new Date(a.entry.startDate);
      const aEnd = new Date(a.entry.endDate);

      for (let j = i + 1; j < group.length; j++) {
        const b = group[j];
        const bStart = new Date(b.entry.startDate);
        const bEnd = new Date(b.entry.endDate);

        const overlap = aStart <= bEnd && bStart <= aEnd;
        if (overlap) {
          conflictIndexes.add(a.index);
          conflictIndexes.add(b.index);
        }
      }
    }
  });

  conflictIndexes.forEach((index) => {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message,
      path: [pathPrefix, index, field]
    });
  });
};

export const validateDateRangesOverlapWithAttainments = (
  entries: {
    startDate?: string;
    endDate?: string;
    attainmentId?: number | string | null;
  }[],
  ctx: RefinementCtx,
  pathPrefix: string,
  message: 'Периоды действия навыка пересекаются с другим периодом этого же навыка',
  field: string = 'overlappingError'
) => {
  const conflictIndexes = new Set<number>();
  const entriesByAttainment: Record<
    string,
    Array<{
      entry: any;
      index: number;
    }>
  > = {};

  entries.forEach((entry, index) => {
    if (!entry.attainmentId) return;
    
    const attainmentId = entry.attainmentId.toString();

    if (!entriesByAttainment[attainmentId]) {
      entriesByAttainment[attainmentId] = [];
    }

    entriesByAttainment[attainmentId].push({
      entry,
      index
    });
  });

  Object.values(entriesByAttainment).forEach((group) => {
    for (let i = 0; i < group.length; i++) {
      const a = group[i];
      if (!a.entry.startDate) continue;
      const aStart = new Date(a.entry.startDate);
      const aEnd = a.entry.endDate ? new Date(a.entry.endDate) : null;

      for (let j = i + 1; j < group.length; j++) {
        const b = group[j];
        
        if (!b.entry.startDate) continue;
        
        const bStart = new Date(b.entry.startDate);
        const bEnd = b.entry.endDate ? new Date(b.entry.endDate) : null;

        const overlap = 
          (!aEnd || bStart < aEnd) && 
          (!bEnd || aStart < bEnd);

        if (overlap) {
          conflictIndexes.add(a.index);
          conflictIndexes.add(b.index);
        }
      }
    }
  });

  conflictIndexes.forEach((index) => {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message,
      path: [pathPrefix, index, field]
    });
  });
};